import { camelCase, isNumber } from 'lodash';

import { GetReportsPresentationResponse } from '@appTypes/webscript';
import { REPORTS_BASE_URL } from '@config';
import {
  NEW_PROGRESS_REPORT,
  NEW_ROSTER_REPORT,
} from '@modules/reports/constants';
import { tagTypes } from '@modules/rtk/constants';
import { getPreferredName } from '@modules/shared/utils/preferredName';

export const sblSharedReportsManagerContainer = {
  Method: 'ExecuteContainerAPI',
  Container: 'SBLSharedReportsManagerAPI',
} as const;

export const sblRosterReportsManagerContainer = {
  Method: 'ExecuteContainerAPI',
  Container: 'SBRosterReportsManagerAPI',
} as const;

export type CustomReportsPOSTPayload<T> = {
  unitId: string;
  extUnitIds?: string[];
  payload: T;
};

export type CustomReportType =
  | typeof tagTypes.CustomReports
  | typeof tagTypes.CustomRosterReports;

const invalidateCustomReportsTag =
  (tag: CustomReportType) =>
  (
    _result: unknown,
    error: unknown,
    { unitId, extUnitIds }: { unitId: string; extUnitIds?: string[] },
  ) => {
    if (error) return [];

    const tagsToInvalidate = [
      {
        type: tag,
        id: unitId,
      },
    ];

    if (extUnitIds?.length) {
      const additionalTagInvalidations = extUnitIds.map(extUnitId => ({
        type: tag,
        id: extUnitId,
      }));
      tagsToInvalidate.push(...additionalTagInvalidations);
    }

    return tagsToInvalidate;
  };

export type CustomReportsOperations = 'share' | 'unshare' | 'delete';

type CustomReportsPOSTApis =
  | 'ShareSelectedReports'
  | 'DeleteSelectedReports'
  | 'UnshareSelectedReports'
  | 'HideSelectedReports'
  | 'ShowSelectedReports';

export const postCustomReportBody = <T>(api: CustomReportsPOSTApis) => ({
  query: (arg: { unitId: string; extUnitId: string; payload: T }) => ({
    url: '',
    params: {
      ...sblSharedReportsManagerContainer,
      API: api,
    },
    method: 'POST',
    body: arg.payload,
  }),
  invalidatesTags: invalidateCustomReportsTag(tagTypes.CustomReports),
});

export const postCustomRosterReportBody = <T>(api: CustomReportsPOSTApis) => ({
  query: (arg: { unitId: string; payload: T }) => ({
    url: '',
    params: {
      ...sblRosterReportsManagerContainer,
      API: api,
    },
    method: 'POST',
    body: arg.payload,
  }),
  invalidatesTags: invalidateCustomReportsTag(tagTypes.CustomRosterReports),
});

export interface CustomReportListItem {
  type: CustomReportType;
  reportId: number;
  isShared: boolean;
  unit: string;
  title: string;
  userName: string;
  userMid: string;
  createdDate: string;
  designUri: string;
  runUri: string;
}

export interface UnitLink {
  unitId: number;
  unitName: string;
  linkCaption: string;
  hasReports: boolean;
  hasSharedReports: boolean;
  linkUri: string;
}

export interface NormalizedCustomReports {
  type: CustomReportType;
  header: string;
  generated: string;
  unit: string;
  council: string;
  district: string;
  charteredOrganization: string;
  reports: CustomReportListItem[];
  unitLinks: UnitLink[];
}

export function transformCustomReportsResponse(
  response: GetReportsPresentationResponse,
  type: CustomReportType,
): NormalizedCustomReports {
  const reports = response.reports.map(report => ({
    type,
    reportId: Number(report.report_id),
    isShared: report.is_report_shared,
    unit: report.unit,
    title: report.report_title,
    userName: getPreferredName({
      firstName: report.user_first_name,
      lastName: report.user_last_name,
    }),
    userMid: report.user_member_id,
    createdDate: report.created_date,
    designUri: report.design_uri,
    runUri: report.run_uri,
  }));

  const unitLinks = response.unit_links.map(report => ({
    unitId: Number(report.unit_id),
    unitName: report.unit_name,
    linkCaption: report.link_caption,
    hasReports: report.has_reports,
    hasSharedReports: report.has_shared_reports,
    linkUri: report.link_uri,
  }));

  return {
    type,
    header: response.header,
    generated: response.generated,
    unit: response.unit,
    council: response.council,
    district: response.district,
    charteredOrganization: response.chartered_organization,
    reports,
    unitLinks,
  };
}

export const getCustomReportButtonMessage = (type: CustomReportsOperations) =>
  `reports.customReport.${type}`;

export const getNewRosterReportUri = ({
  unitTypeId,
  orgGuid,
}: {
  unitTypeId: number;
  orgGuid: string | null;
}) =>
  `${REPORTS_BASE_URL}${NEW_ROSTER_REPORT}&UnitTypeID=${unitTypeId}${
    orgGuid ? `&OrganizationGUID=${orgGuid}` : ''
  }`;

export const getNewProgressReportUri = ({
  unitTypeId,
  orgGuid,
}: {
  unitTypeId: number;
  orgGuid: string | null;
}) =>
  `${REPORTS_BASE_URL}${NEW_PROGRESS_REPORT}&UnitTypeID=${unitTypeId}${
    orgGuid ? `&OrganizationGUID=${orgGuid}` : ''
  }`;

export function mergeUnitLinks(
  customReportsData: UnitLink[],
  customRosterReportsData: UnitLink[],
): UnitLink[] {
  const map = new Map<number, UnitLink>();

  [...customReportsData, ...customRosterReportsData].forEach(item => {
    const existing = map.get(item.unitId);

    if (!existing) {
      map.set(item.unitId, item);
    } else {
      map.set(item.unitId, {
        ...existing,
        hasReports: existing.hasReports || item.hasReports,
        hasSharedReports: existing.hasSharedReports || item.hasSharedReports,
      });
    }
  });

  return Array.from(map.values());
}

interface GroupedSelectedReports {
  customReports: number[];
  customRosterReports: number[];
  extUnitIds: string[];
}

type SplitSelectedReportsByType = {
  unitLinksMap: Record<string, UnitLink>;
  mainUnitId: number;
};

export const splitSelectedReportsByType = (
  reports: CustomReportListItem[],
  selectedReportIds: number[],
  extUnitIdsUtils?: SplitSelectedReportsByType,
): GroupedSelectedReports => {
  const result: GroupedSelectedReports = {
    customReports: [],
    customRosterReports: [],
    extUnitIds: [],
  };

  const unitNamesWithSelectedReports = new Set<string>();

  reports.forEach(report => {
    if (selectedReportIds.includes(report.reportId)) {
      const key = camelCase(report.type) as keyof Omit<
        GroupedSelectedReports,
        'extUnitIds'
      >;
      result[key].push(report.reportId);
      unitNamesWithSelectedReports.add(report.unit);
    }
  });

  if (extUnitIdsUtils) {
    const { unitLinksMap, mainUnitId } = extUnitIdsUtils;
    result.extUnitIds = Array.from(unitNamesWithSelectedReports)
      .map(name => unitLinksMap[name]?.unitId)
      .filter(id => isNumber(id) && id !== mainUnitId)
      .map(id => `${id}`);
  }

  return result;
};
